import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Sidebar } from './sidebar/sidebar';
import { filter, Subject, takeUntil } from 'rxjs';
import { ConfigDataService } from '../../services/config.data.service';
import { ConfigService } from '../../../services/config.service';
import { Spinner } from '../../components/spinner/spinner.component';
import { SpinnerService } from '../../services/spinner.service';
import { Dialog } from '../../components/ui/dialog/dialog';
import { Headline } from '../../components/ui/headline/headline';
import { Alignment, HTag } from '../../components/ui/headline/headline.model';
import { ErrorService } from '../../services/error.service';
import { IConfig } from '../../interfaces/config.interface';
import { Button } from '../../components/ui/button/button';
import { CognitoService } from '../../services/cognito.service';
import { InactivityService } from '../../services/inactivity.service';

@Component({
  selector: 'app-base',
  imports: [Sidebar, RouterOutlet, Spinner, Dialog, Headline, Button],
  templateUrl: './base.html',
  styleUrl: './base.scss',
})
export class Base implements OnInit, OnDestroy {
  private configService = inject(ConfigService);
  private configDataService = inject(ConfigDataService);
  private spinnerService = inject(SpinnerService);
  private errorService = inject(ErrorService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private readonly cognitoService = inject(CognitoService);
  private readonly inactivityService = inject(InactivityService);

  private destroy$ = new Subject<void>();

  readonly hTag = HTag;
  readonly alignment = Alignment;

  spinnerState = this.spinnerService.state;
  hasTechnicalError = this.errorService.hasTechnicalError;
  inlineTechnicalError = this.errorService.inlineTechnicalError;
  config = signal<IConfig | undefined>(undefined);
  pageTitle = signal<string>('');
  sessionExpired = this.inactivityService.sessionExpired;

  ngOnInit(): void {
    this.loadConfig();
    this.listenToRouteChanges();
    this.inactivityService.startWatching();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.inactivityService.stopWatching();
  }

  refreshPage() {
    window.location.reload();
  }

  private loadConfig() {
    try {
      this.configDataService.setLoading(true);
      this.configService.getConfig().subscribe((config) => {
        this.configDataService.setConfig(config ?? undefined);
        this.config.set(config ?? undefined);
      });
    } catch (error) {
      console.error('Error loading config:', error);
      this.configDataService.setConfig(undefined);
      this.config.set(undefined);
    } finally {
      this.configDataService.setLoading(false);
    }
  }

  private listenToRouteChanges() {
    // Get initial route title
    this.updatePageTitle();

    // Listen to route changes
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.updatePageTitle();
      });
  }

  private updatePageTitle() {
    let route = this.activatedRoute.firstChild;
    while (route?.firstChild) {
      route = route.firstChild;
    }
    const title = route?.snapshot.data['title'] || '';
    this.pageTitle.set(title);
  }

  signOut(): void {
    this.cognitoService.signOut().subscribe({
      error: () => {
        this.errorService.hasTechnicalError.set(true);
      },
    });
  }

  handleReconnect(): void {
    this.inactivityService.reset();
    this.inactivityService.stopWatching();
    this.signOut();
  }
}
